import { formatDate } from '../../utils/dateFormatter';

const buildHtml = ({ client, invoices, payments, fromDate, toDate, language }) => {
  const isAr = language === 'ar';
  const dir = isAr ? 'rtl' : 'ltr';

  const totalInvoiced = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const outstanding = totalInvoiced - totalPaid;

  const title = isAr ? 'كشف حساب' : 'Account Statement';
  const companyName = 'CLADAKIS FOR READY MADE GARMENTS';

  const invoiceRows = invoices.map((inv, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${formatDate(inv.date)}</td>
      <td>#${inv.invoice_number}</td>
      <td>${inv.invoice_type || 'invoice'}</td>
      <td class="num">${(inv.total || 0).toFixed(2)}</td>
    </tr>
  `).join('');

  const paymentRows = payments.map((p, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${formatDate(p.date)}</td>
      <td class="num">${(p.amount || 0).toFixed(2)}</td>
      <td>${p.notes || '—'}</td>
    </tr>
  `).join('');

  return `<!DOCTYPE html>
<html dir="${dir}">
  <head>
    <meta charset="UTF-8" />
    <title>${title} — ${client.name}</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; padding: 30px; color: #000; background: #fff; direction: ${dir}; }
      .container { max-width: 800px; margin: 0 auto; }
      .header { text-align: center; margin-bottom: 30px; padding-bottom: 15px; border-bottom: 2px solid #333; }
      .header h1 { font-size: 20pt; margin-bottom: 4px; }
      .header h2 { font-size: 15pt; margin: 8px 0; }
      .header p { font-size: 10pt; color: #555; }
      .meta { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 10pt; }
      .meta div { line-height: 1.7; }
      h3 { font-size: 12pt; margin: 20px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 20px; font-size: 10pt; }
      th { background: #f0f0f0; border: 1px solid #ccc; padding: 8px; text-align: ${isAr ? 'right' : 'left'}; font-weight: bold; }
      td { border: 1px solid #ccc; padding: 8px; }
      .num { text-align: ${isAr ? 'left' : 'right'}; font-family: monospace; direction: ltr; }
      .summary { background: #f8f8f8; border: 1px solid #ccc; padding: 16px; border-radius: 4px; margin-top: 10px; }
      .summary-row { display: flex; justify-content: space-between; margin-bottom: 6px; font-size: 11pt; }
      .summary-row.total { border-top: 2px solid #333; padding-top: 8px; margin-top: 6px; font-size: 13pt; font-weight: bold; }
      .outstanding-positive { color: #b45309; }
      .outstanding-zero { color: #16a34a; }
      .footer { margin-top: 50px; border-top: 1px solid #ccc; padding-top: 12px; text-align: center; font-size: 9pt; color: #666; }
      @media print { body { padding: 15px; } }
    </style>
  </head>
  <body>
    <div class="container">
      <div class="header">
        <h1>${companyName}</h1>
        <h2>${title}</h2>
        <p>${isAr ? 'كشف حساب مفصّل' : 'Detailed Account Statement'}</p>
      </div>
      <div class="meta">
        <div>
          <strong>${isAr ? 'العميل' : 'Client'}:</strong> ${client.name}<br/>
          ${client.phone ? `<strong>${isAr ? 'الهاتف' : 'Phone'}:</strong> ${client.phone}<br/>` : ''}
          ${client.email ? `<strong>${isAr ? 'البريد' : 'Email'}:</strong> ${client.email}` : ''}
        </div>
        <div style="text-align:${isAr ? 'left' : 'right'}">
          <strong>${isAr ? 'من' : 'From'}:</strong> ${formatDate(fromDate)}<br/>
          <strong>${isAr ? 'إلى' : 'To'}:</strong> ${formatDate(toDate)}<br/>
          <strong>${isAr ? 'تاريخ الطباعة' : 'Printed'}:</strong> ${formatDate(new Date())}
        </div>
      </div>

      <h3>${isAr ? 'الفواتير' : 'Invoices'}</h3>
      ${invoices.length > 0 ? `
      <table>
        <thead>
          <tr>
            <th style="width:40px">#</th>
            <th>${isAr ? 'التاريخ' : 'Date'}</th>
            <th>${isAr ? 'رقم الفاتورة' : 'Invoice #'}</th>
            <th>${isAr ? 'النوع' : 'Type'}</th>
            <th>${isAr ? 'المبلغ' : 'Amount'}</th>
          </tr>
        </thead>
        <tbody>${invoiceRows}</tbody>
      </table>` : `<p style="color:#888;font-size:10pt;margin-bottom:16px">${isAr ? 'لا توجد فواتير في هذه الفترة' : 'No invoices in this period'}</p>`}

      <h3>${isAr ? 'المدفوعات' : 'Payments'}</h3>
      ${payments.length > 0 ? `
      <table>
        <thead>
          <tr>
            <th style="width:40px">#</th>
            <th>${isAr ? 'التاريخ' : 'Date'}</th>
            <th>${isAr ? 'المبلغ' : 'Amount'}</th>
            <th>${isAr ? 'ملاحظات' : 'Notes'}</th>
          </tr>
        </thead>
        <tbody>${paymentRows}</tbody>
      </table>` : `<p style="color:#888;font-size:10pt;margin-bottom:16px">${isAr ? 'لا توجد مدفوعات في هذه الفترة' : 'No payments in this period'}</p>`}

      <div class="summary">
        <div class="summary-row">
          <span>${isAr ? 'إجمالي الفواتير' : 'Total Invoiced'}:</span>
          <span style="direction:ltr">${totalInvoiced.toFixed(2)} EGP</span>
        </div>
        <div class="summary-row">
          <span>${isAr ? 'إجمالي المدفوعات' : 'Total Paid'}:</span>
          <span style="direction:ltr">-${totalPaid.toFixed(2)} EGP</span>
        </div>
        <div class="summary-row total">
          <span>${isAr ? 'الرصيد المستحق' : 'Outstanding Balance'}:</span>
          <span class="${outstanding > 0 ? 'outstanding-positive' : 'outstanding-zero'}" style="direction:ltr">${outstanding.toFixed(2)} EGP</span>
        </div>
      </div>

      <div class="footer">
        <p>${isAr ? 'كلاداكيس للملابس الجاهزة' : 'Cladakis for Ready Made Garments'}</p>
      </div>
    </div>
    <script>
      window.onload = function() { window.print(); window.onafterprint = function() { window.close(); } }
    </script>
  </body>
</html>`;
};

export const useClientStatementPrint = () => {
  const handlePrint = (params) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(buildHtml(params));
    printWindow.document.close();
  };

  const handleDownload = (params) => {
    const html = buildHtml(params);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Statement_${params.client.name}_${params.fromDate}_${params.toDate}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return { handlePrint, handleDownload };
};
